import Vue from 'vue'
import CompositionApi from '@vue/composition-api'
import { computed, reactive } from '@vue/composition-api'
import { Route, Coordinates } from '@/types'
import { langeroute } from '@/assets/routes/lang'
import { kidsroute } from '@/assets/routes/kids'
import { testroute } from '@/assets/routes/test'
import { UnwrapRef } from '@vue/composition-api/dist/reactivity'
import { getDistance } from '@/lib/mapbox'

Vue.use(CompositionApi)

interface GlobalStore {
  mode: 'intro' | 'riding'
  activeRouteId: string | null
  popoverStepIndex: number | null
  instructionStepIndex: number | null
  locationAccess: boolean
  currentLocation: Coordinates
  locationAccuracy: number | null
  routes: Route[]
  debugging: boolean
}

const state: UnwrapRef<GlobalStore> = reactive({
  mode: 'intro',
  activeRouteId: 'langeroute',
  popoverStepIndex: null,
  instructionStepIndex: null,
  locationAccess: false,
  currentLocation: {
    lat: 0,
    lon: 0
  },
  locationAccuracy: null,
  routes: [langeroute, kidsroute, testroute],
  debugging: false
})

// Getters

const activeRoute = computed(() => {
  if (state.activeRouteId) {
    return state.routes.find(route => route.id === state.activeRouteId)
  }
})

// Actions

const askLocationPermissions = () => {
  navigator.geolocation.watchPosition(updateGeolocation, errorGeolocation, {
    enableHighAccuracy: true
  })
}

const updateGeolocation = (position: Position) => {
  state.locationAccess = true
  if (!state.debugging) {
    state.currentLocation = {
      lon: position.coords.longitude,
      lat: position.coords.latitude
    }
    state.locationAccuracy = position.coords.accuracy
  }
}

const errorGeolocation = (error: PositionError) => {
  state.locationAccess = false

  alert(
    'Er ging iets fout. Zorg ervoor dat je browser toegang heeft tot je locatie en probeer opnieuw.'
  )
  console.log(error)
}

const updateElapsedRoute = (
  currentLocation: Coordinates,
  coordinates: number[][]
) => {
  const distances = coordinates.map(coordinate => {
    return Math.sqrt(
      Math.pow(coordinate[0] - currentLocation.lon, 2) +
        Math.pow(coordinate[1] - currentLocation.lat, 2)
    )
  })

  const routeStepsDistances = activeRoute.value?.steps.map(step => {
    return Math.sqrt(
      Math.pow(step.coordinates.lon - currentLocation.lon, 2) +
        Math.pow(step.coordinates.lat - currentLocation.lat, 2)
    )
  })

  const minDistance = distances.reduce((a, b) => {
    return Math.min(a, b)
  })

  const minRouteStepsDistance = routeStepsDistances?.reduce((a, b) => {
    return Math.min(a, b)
  })

  const closestIndex = distances.indexOf(minDistance)
  const closestRouteStepIndex =
    (minRouteStepsDistance &&
      routeStepsDistances?.indexOf(minRouteStepsDistance)) ||
    0

  const closestRouteStep = activeRoute.value?.steps[closestRouteStepIndex]
  const distanceInMeters =
    (closestRouteStep &&
      getDistance(
        closestRouteStep.coordinates.lat,
        closestRouteStep.coordinates.lon,
        currentLocation.lat,
        currentLocation.lon
      ) * 100000) ||
    0

  if (distanceInMeters < 100) {
    state.instructionStepIndex = closestRouteStepIndex
  } else {
    state.instructionStepIndex = null
  }

  return coordinates.slice(0, closestIndex + 1)
}

const checkDistance = (
  targetCoordinates: Coordinates,
  requiredDistanceInMeters: number
) => {
  const distance =
    getDistance(
      targetCoordinates.lat,
      targetCoordinates.lon,
      state.currentLocation.lat,
      state.currentLocation.lon
    ) * 100000
  return distance < requiredDistanceInMeters
}

const startRoute = (routeId: string) => {
  state.activeRouteId = routeId
  state.mode = 'riding'
}

const stopRoute = () => {
  state.popoverStepIndex = null
  state.mode = 'intro'
}

export function useStore() {
  return {
    state,
    activeRoute,
    askLocationPermissions,
    updateElapsedRoute,
    checkDistance,
    startRoute,
    stopRoute
  }
}
